import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { Platform } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';

import { Store } from '@ngxs/store';
import { WitnessSignatureService } from '@pages/signature-witness/witness-signature.service';
import { WitnessSignatureRequest } from '@shared/models/interfaces/witnessSignature.interface';
import { IpService } from '@shared/providers/ip.service';
import { UtilsService } from '@shared/providers/utils.service';
import {
  SetWitnessSignatureRequestData,
  SetWitnessSignatureResponseData,
} from '@shared/store/witness/witness.actions';
import { CPFValidator } from '@shared/validators/cpf.validator';
import { saveAs } from 'file-saver';
import moment from 'moment';

@Component({
  selector: 'app-first-step',
  templateUrl: './first-step.page.html',
  styleUrls: ['./first-step.page.scss'],
})
export class FirstStepPage implements OnInit {
  public signatureForm: FormGroup;
  public signatureReq: WitnessSignatureRequest;
  public ip: string;
  public signatureId: string;
  public link: string;
  public email: string;
  public downloadLink;
  public maxDate: string;
  public formSubmitted: boolean = false;

  public loading = true;

  constructor(
    private signatureService: WitnessSignatureService,
    private router: Router,
    private formBuilder: FormBuilder,
    private utils: UtilsService,
    private ipService: IpService,
    private activatedRoute: ActivatedRoute,
    private store: Store,
    private sanitizer: DomSanitizer,
    private translate: TranslateService,
    private platform: Platform
  ) {
    this.maxDate = moment(new Date(new Date().getFullYear() - 17, 11, 31)).format('YYYY-MM-DD');
  }

  get witnessSinatureCpf() {
    return this.signatureForm.get('witnessSinatureCpf');
  }

  get witnessSignatureName() {
    return this.signatureForm.get('witnessSignatureName');
  }

  get witnessSignatureBirthday() {
    return this.signatureForm.get('witnessSignatureBirthday');
  }

  async ngOnInit() {
    this.signatureForm = this.initializeForm();
    try {
      this.signatureId = this.activatedRoute.snapshot.paramMap.get('id');

      const { ip } = await this.ipService.getIPAddress();
      const { witnessSignature } = await this.signatureService.getWitnessSignatureData(this.signatureId);

      this.link = witnessSignature.surveyReportLink;
      this.email = witnessSignature.witnessEmail;
      this.signatureReq = {
        witnessSignatureIp: ip,
        witnessSinatureCpf: witnessSignature.interveningCpf,
      };
      this.store.dispatch(new SetWitnessSignatureRequestData(this.signatureReq));
      this.store.dispatch(new SetWitnessSignatureResponseData(witnessSignature));

      if (witnessSignature.witnessHasSigned) {
        await this.goToDone();
      } else if (witnessSignature.tokenRequested && !witnessSignature.witnessHasSigned) {
        this.gotToSecondStep();
      }
    } catch (error) {
      console.error(error);
      this.router.navigate(['testemunha', 'assinar', 'erro']);
    } finally {
      this.loading = false;
    }
    this.downloadLink = this.sanitizer.bypassSecurityTrustResourceUrl(this.link);
  }

  private gotToSecondStep() {
    this.router.navigate(['testemunha', 'assinar', this.signatureId, 'token'], {
      state: { email: this.email },
    });
  }

  async goToDone() {
    await this.router.navigate(['assinar', 'concluido']);
  }

  initializeForm() {
    return this.formBuilder.group({
      witnessSinatureCpf: ['', Validators.compose([Validators.required, CPFValidator.isValid])],
      witnessSignatureName: ['', Validators.compose([Validators.required, Validators.maxLength(90)])],
      witnessSignatureBirthday: [
        '',
        Validators.compose([Validators.required, this.maxDateValidator(this.maxDate)]),
      ],
    });
  }

  download() {
    if (this.platform.is('cordova')) {
      saveAs(this.link, 'relatorio.pdf');
    } else {
      window.open(this.link, '_blank');
    }
  }

  next(form: FormGroup) {
    this.formSubmitted = true;
    if (form.invalid) {
      this.utils.validateAllFields(form);
      this.utils.showToast('', `${this.translate.instant('SIGNATURE.CHECK_THE_DATA_ENTERED')}`);
    } else {
      const formData = form.getRawValue();
      const signatureId = this.activatedRoute.snapshot.paramMap.get('id');
      this.signatureReq.witnessSinatureCpf = this.utils.unmask(formData.witnessSinatureCpf);
      this.signatureReq.witnessSignatureName = this.witnessSignatureName.value;

      // Transformando a data inserida no input para ISO String
      const formattedDate = moment(this.witnessSignatureBirthday.value, 'DD/MM/YYYY').toISOString();
      this.signatureReq.witnessSignatureBirthday = formattedDate ?? null;

      this.signatureService
        .update(this.signatureReq, signatureId)
        .then(() => {
          this.store.dispatch(new SetWitnessSignatureRequestData(this.signatureReq));
          this.gotToSecondStep();
        })
        .catch(result => {
          if (result.error.code === 'intervening_signature/ip-required') {
            this.utils.showToast('', `${this.translate.instant('SIGNATURE.WE_CANT_GET_IP')}`);
          } else {
            this.utils.showToast('', result.error.message);
          }
        });
    }
  }

  // Função de Validador Personalizado para MaxDate
  private maxDateValidator(maxDate: string): ValidatorFn {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      if (control.value) {
        const inputDate = moment(control.value, 'DD/MM/YYYY', true);
        const max = moment(maxDate, 'YYYY-MM-DD');
        if (!inputDate.isValid()) {
          return { invalidDate: true };
        }
        if (inputDate.isAfter(max)) {
          return { maxDate: true };
        }
      }
      return null;
    };
  }
}
